import { getProvider } from '@galapagos/observability';
import {
  buildBaselineCheckInItems,
  hasImportableBaselineDays,
  type OnboardingSessionData,
} from '@galapagos/shared';
import { DateTime } from 'luxon';
import type { SignupApi } from '../services/signup-api/signup-api';
import { memberTimeZone } from './booking-slots';

// The backfill is best-effort, so nothing here reaches the member. These
// signals are the only way operators learn a reported week was not saved, and
// they carry a closed reason and nothing about the member.
function reportEmpty(reason: 'no_day_answers' | 'no_program_id'): void {
  getProvider().logs.log('warn', 'galapagos.signup.baseline_checkins_empty', { reason });
}

function reportFailed(): void {
  getProvider().logs.log('warn', 'galapagos.signup.baseline_checkins_failed', {
    reason: 'rejected',
  });
}

/**
 * Post the reported week in a just-finalized session as check-ins for the
 * program the finalize enrolled. Started, never awaited: wrap-up must not wait
 * on it, and the server's per-date correcting append makes a repeat safe.
 */
export function backfillBaselineCheckIns(api: SignupApi, session: OnboardingSessionData): void {
  const answers = session.answers;
  const memberProgramId = session.memberProgramId ?? null;
  if (memberProgramId === null) {
    // No program and nothing to import is the ordinary no-op; a reported week
    // with no program to write it to is the loss worth reporting.
    if (hasImportableBaselineDays(answers)) reportEmpty('no_program_id');
    return;
  }
  // Only the fallback when the session carries no week anchor: today on the
  // member's own calendar.
  const today = DateTime.now().setZone(memberTimeZone()).toISODate() ?? '';
  const items = buildBaselineCheckInItems(memberProgramId, answers, today);
  if (items.length === 0) {
    reportEmpty('no_day_answers');
    return;
  }
  api.submitCheckIns({ items }).catch(reportFailed);
}
